import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { propertiesAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Building2,
  MapPin,
  Edit,
  Trash2,
  Loader2,
  Search
} from 'lucide-react';

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Deutschland',
    building_year: '',
    total_units: 1,
    total_area: '',
    owner_name: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await propertiesAPI.getAll();
      setProperties(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Immobilien');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'Deutschland',
      building_year: '',
      total_units: 1,
      total_area: '',
      owner_name: ''
    });
    setEditingProperty(null);
  };

  const openEditDialog = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      address: property.address,
      city: property.city,
      postal_code: property.postal_code,
      country: property.country,
      building_year: property.building_year || '',
      total_units: property.total_units,
      total_area: property.total_area || '',
      owner_name: property.owner_name || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        building_year: formData.building_year ? parseInt(formData.building_year) : null,
        total_units: parseInt(formData.total_units),
        total_area: formData.total_area ? parseFloat(formData.total_area) : 0,
        owner_name: formData.owner_name || null
      };

      if (editingProperty) {
        await propertiesAPI.update(editingProperty.id, data);
        toast.success('Immobilie aktualisiert');
      } else {
        await propertiesAPI.create(data);
        toast.success('Immobilie erstellt');
      }

      setIsDialogOpen(false);
      resetForm();
      loadProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await propertiesAPI.delete(deleteId);
      toast.success('Immobilie gelöscht');
      loadProperties();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredProperties = properties.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="properties-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Immobilien
            </h1>
            <p className="text-slate-500 mt-1">Verwalten Sie Ihre Immobilien</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-property-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus className="mr-2 h-4 w-4" />
                Immobilie hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {editingProperty ? 'Immobilie bearbeiten' : 'Neue Immobilie'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid="property-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="z.B. Musterstraße 1"
                      required
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">
                      Straße & Hausnummer <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address"
                      data-testid="property-address-input"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Musterstraße 1"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">
                      PLZ <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="postal_code"
                      data-testid="property-postal-code-input"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="12345"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">
                      Stadt <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      data-testid="property-city-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Musterstadt"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="building_year">Baujahr</Label>
                    <Input
                      id="building_year"
                      data-testid="property-building-year-input"
                      type="number"
                      value={formData.building_year}
                      onChange={(e) => setFormData({ ...formData, building_year: e.target.value })}
                      placeholder="1990"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total_units">
                      Anzahl Einheiten <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="total_units"
                      data-testid="property-units-input"
                      type="number"
                      min="1"
                      value={formData.total_units}
                      onChange={(e) => setFormData({ ...formData, total_units: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total_area">Gesamtfläche (m²)</Label>
                    <Input
                      id="total_area"
                      data-testid="property-area-input"
                      type="number"
                      step="0.01"
                      value={formData.total_area}
                      onChange={(e) => setFormData({ ...formData, total_area: e.target.value })}
                      placeholder="500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_name">Eigentümer</Label>
                    <Input
                      id="owner_name"
                      data-testid="property-owner-input"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      placeholder="Max Mustermann"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="property-save-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="property-search-input"
            placeholder="Suchen nach Name, Adresse oder Stadt..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Properties List */}
        {filteredProperties.length > 0 ? (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Immobilie</TableHead>
                  <TableHead className="font-semibold">Adresse</TableHead>
                  <TableHead className="font-semibold text-right">Einheiten</TableHead>
                  <TableHead className="font-semibold text-right">Fläche</TableHead>
                  <TableHead className="font-semibold">Erstellt</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProperties.map((property) => (
                  <TableRow key={property.id} data-testid={`property-row-${property.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{property.name}</p>
                          {property.owner_name && (
                            <p className="text-sm text-slate-500">{property.owner_name}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-700">{property.address}</p>
                          <p className="text-sm text-slate-500">
                            {property.postal_code} {property.city}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {property.total_units}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {property.total_area > 0 ? `${property.total_area} m²` : '-'}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {formatDate(property.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`edit-property-${property.id}`}
                          onClick={() => openEditDialog(property)}
                        >
                          <Edit className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-property-${property.id}`}
                          onClick={() => setDeleteId(property.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm ? 'Keine Immobilien gefunden' : 'Noch keine Immobilien'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchTerm 
                  ? 'Versuchen Sie einen anderen Suchbegriff' 
                  : 'Fügen Sie Ihre erste Immobilie hinzu, um loszulegen'
                }
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-add-property-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Erste Immobilie hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Immobilie löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Alle zugehörigen 
                Einheiten, Mieter, Kosten und Abrechnungen werden ebenfalls gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-property"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
